import React, { useState, useEffect, memo } from "react";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { submitTargetCost } from "../../utils/actions/userActions";
import { currencyFormatter } from "../../utils/helper";

const TargetCostUserModal = ({
  isTargetCostModalOpen,
  setIsTargetCostModalOpen,
  selectedQuote,
  onSubmitFunction,
  setSelectedQuote,
}) => {
  console.log("TargetCostUserModal rendered");

  const [targetCost, setTargetCost] = useState("");
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const funcName = searchParams.get("funcName");

  useEffect(() => {
    if (funcName === "targetCost") {
      setIsTargetCostModalOpen(true);
    }
  }, [funcName]);

  useEffect(() => {
    return () => {
      setIsTargetCostModalOpen(false);
    };
  }, []);

  const validateInput = (value) => {
    const regex = /^\d+(\.\d{1,2})?$/;
    if (!regex.test(value)) {
      setError("Please enter a valid number with up to 2 decimal places.");
    } else {
      setError("");
    }
    setTargetCost(value);
  };

  const handleSubmit = async () => {
    if (error || !targetCost) {
      toast.error("Please enter a valid target cost");
      return;
    }

    if (!selectedQuote?._id) {
      toast.error("Unable to find quotation");
      return;
    }

    try {
      const response = await submitTargetCost(selectedQuote._id, targetCost);
      if (!response?.success) throw new Error();

      toast.success("Target cost submitted successfully");
      setSelectedQuote((prev) => ({
        ...prev,
        userTargetCost: targetCost,
      }));
    } catch (err) {
      toast.error("An error occurred. Please try again later.");
    }
  };

  const renderSubmittedMessage = () => {
    const submittedCost = selectedQuote?.userTargetCost || "xxxx.xx";
    return (
      <div className="p-6 text-center text-gray-700">
        <p className="mb-2">
          <strong>Thank you!</strong> Your target cost of{" "}
          <strong>{currencyFormatter(submittedCost)}</strong> is currently under review.
        </p>
        <p className="mb-2">
          Our team will reach out to you shortly with an update. In the meantime, if you have any
          questions or require assistance, please feel free to use the{" "}
          <strong>comment</strong> feature to connect with us.
        </p>
        <p>We appreciate your patience!</p>
      </div>
    );
  };

  if (!isTargetCostModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center " style={{ zIndex: 50000 }}>
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {selectedQuote?.userTargetCost
              ? "Target Cost Submitted"
              : "Enter the total target cost you aim to achieve for the project."}
          </h2>
          <button
            onClick={() => setIsTargetCostModalOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {selectedQuote?.userTargetCost ? (
          renderSubmittedMessage()
        ) : (
          <div className="p-6">
            <label htmlFor="targetCostInput" className="block mb-2 text-sm font-medium text-gray-700">
              Enter Target Cost
            </label>
            <input
              id="targetCostInput"
              type="text"
              value={targetCost}
              onChange={(e) => validateInput(e.target.value)}
              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring focus:ring-primaryColor"
              placeholder="e.g. 5000.00"
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
        )}

        {!selectedQuote?.userTargetCost && (
          <div className="flex justify-end items-center px-6 py-4 border-t space-x-3">
            <button
              onClick={() => setIsTargetCostModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
            {!selectedQuote?.userTargetCostsubmited && (
              <button
                onClick={handleSubmit}
                disabled={!!selectedQuote?.userTargetCost}
                className="px-4 py-2 text-sm font-medium text-white bg-primaryColor rounded-md hover:bg-primaryColor disabled:bg-blue-300"
              >
                {selectedQuote?.userTargetCost ? "Target Cost Submitted" : "Submit"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(TargetCostUserModal);
